import { useEffect, useMemo, useState } from 'react'
import {
  Button,
  FlexCell,
  FlexRow,
  FlexSpacer,
  LabeledInput,
  PickerInput,
  TextArea,
  DatePicker,
  TimePicker,
  useForm,
} from '@epam/loveship'
import { useArrayDataSource, useUuiContext, UuiContexts } from '@epam/uui-core'
import { SidebarPanelHeader } from '../../../../../../components/SidebarPanel/components/SidebarPanelHeader'
import { SidebarPanelMain } from '../../../../../../components/SidebarPanel/components/SidebarPanelMain'
import {
  EventResponse,
  ExtendedEvent,
  NewEvent,
} from '../../../../../../constants/types'
import {
  LOCATION_NOTFOUND_MESSAGE,
  MAX_CHAR_LIMIT,
} from '../../../../../../constants/values'
import { useActions } from '../../../../../../hooks/redux/actions'
import { useFetchLocationAddress } from '../../../../../../hooks/useFetchLocationAddress'
import { useSideBarControlls } from '../../../../../../hooks/useSideBarControlls'
import { useAppSelector } from '../../../../../../hooks/redux/redux'
import {
  useGetCategoriesQuery,
  usePutEventMutation,
} from '../../../../../../store'
import { showErrorNotification } from '../../../../../../utils/notifications/showErrorNotification'
import { showSuccessNotification } from '../../../../../../utils/notifications/showSuccessNotification'
import { showLeavePointCreationModal } from '../../../../../../utils/modals/showLeavePointCreationModal'
import {
  getLanguageByCode,
  getLanguagesArray,
} from '../../../../../../utils/common/getLanguages'
import {
  getDateTimeForPicker,
  getDateTimeString,
} from '../../../../../../utils/common/parseDateTime'
import { checkHttpUrl } from '../../../../../../utils/common/checkHttpUrl'
import css from '../NewEventForm/NewEventForm.module.scss'

type EventEditFormProps = { event: ExtendedEvent }

export const EventEditForm = ({ event }: EventEditFormProps) => {
  const {
    id,
    categoryId,
    name,
    language,
    details,
    lat,
    lng,
    link,
    time: incomingCombinedDateTime,
  } = event
  const {
    date,
    hours = 0,
    minutes = 0,
  } = getDateTimeForPicker(incomingCombinedDateTime)

  const { data: categories } = useGetCategoriesQuery()
  const newEventPosition = useMemo(() => ({ lat, lng }), [lat, lng])
  const { isSideBarCloseInitiated } = useAppSelector((state) => state.app)
  const { closeEditEvent } = useSideBarControlls()
  const {
    addCurrentEvent,
    removeCurrentEvent,
    setSideBarClosingInitiated,
    setEditEventPosition,
  } = useActions()

  const lang = getLanguageByCode(language)
  const { editEventPosition } = useAppSelector((state) => state.events)

  const address = useFetchLocationAddress(editEventPosition)

  useEffect(() => {
    if (isSideBarCloseInitiated) onCloseEditEventForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSideBarCloseInitiated])

  const svc = useUuiContext<UuiContexts>()
  const [putEvent] = usePutEventMutation()

  const onCloseEditEventForm = () => {
    setSideBarClosingInitiated(false)
    showLeavePointCreationModal(svc).then(() => {
      closeEditEvent()
    })
  }

  const { lens, save } = useForm<NewEvent>({
    value: {
      eventName: name,
      eventDescription: details,
      eventCategory: categoryId,
      eventDate: date,
      eventTime: { hours, minutes },
      eventLink: `${link}`,
      eventLocation: address.location,
      countryCode: address.countryCode,
      eventLanguage: lang,
    },
    // Passed null to prevent show "Save Changes?" dialog after history.push
    // Expected behaviour with null: always saves changes when unmount
    beforeLeave: null,
    onSave: (newEvent) => {
      const combinedDateTimeString = getDateTimeString(
        newEvent.eventDate,
        newEvent.eventTime
      )
      setEditEventPosition(null)
      const bodyNewEvent = {
        id,
        categoryId: newEvent.eventCategory ?? 0,
        name: newEvent.eventName,
        details: newEvent.eventDescription,
        time: combinedDateTimeString,
        lat: editEventPosition?.lat ?? newEventPosition?.lat,
        lng: editEventPosition?.lng ?? newEventPosition?.lng,
        language: newEvent.eventLanguage.code,
        link: newEvent.eventLink ?? '',
        location: newEvent.eventLocation,
        countryCode: address.countryCode,
      }
      return putEvent(bodyNewEvent)
        .unwrap()
        .then((value) => {
          if (value) {
            removeCurrentEvent(id)
            addCurrentEvent(value as EventResponse)
          }
          return value
        })
    },
    onSuccess: (result) => {
      closeEditEvent()
      showSuccessNotification({ svc, message: 'Event saved' })
    },
    onError: (error) => {
      showErrorNotification({
        svc,
        message: `Error on save: ${error.message}`,
      })
    },
    getMetadata: () => ({
      props: {
        eventName: {
          isRequired: true,
          validators: [
            (val) => [!val.length && 'Please fill in the Event Name field'],
          ],
        },
        eventCategory: {
          isRequired: true,
          validators: [
            (val) => [!val && 'Please choose a Category to proceed'],
          ],
        },
        eventLanguage: {
          isRequired: true,
          validators: [(val) => [!val && 'Please fill in the Language field']],
        },
        eventLocation: {
          isRequired: true,
          validators: [(val) => [!val && 'Please fill in the Location field']],
        },
        eventDate: {
          isRequired: true,
          validators: [
            (val) => {
              const currentDate = new Date()
              const selectedDate = new Date(val)
              currentDate.setHours(0, 0, 0, 0) // Set the time portion of currentDate to 00:00:00

              return [
                !val && 'Please fill in the Event date field',
                selectedDate < currentDate &&
                  'Event can be added only for upcoming time, please verify date of event',
              ].filter(Boolean)
            },
          ],
        },
        eventTime: {
          isRequired: true,
          validators: [
            (val, formState) => {
              const currentTime = new Date().getTime()
              const combinedDateTimeString = `${formState.eventDate}T${val.hours
                .toString()
                .padStart(2, '0')}:${val.minutes.toString().padStart(2, '0')}`
              const combinedDateTime = new Date(
                combinedDateTimeString
              ).getTime()

              return [
                !val && 'Please fill in the Event time field',
                combinedDateTime < currentTime &&
                  formState.eventDate ===
                    new Date().toISOString().split('T')[0] &&
                  'Event time cannot be in the past',
              ].filter(Boolean)
            },
          ],
        },
        eventDescription: {
          isRequired: true,
          validators: [
            (val) => [
              !val.length && 'Please fill in the Event Description field',
            ],
          ],
        },
        eventLink: {
          isRequired: false,
          validators: [
            (val) => [!!val && !checkHttpUrl(val) && 'Please enter valid url'],
          ],
        },
      },
    }),
  })

  const [eventCategory, setEventCategory] = useState(
    lens.prop('eventCategory').get()
  )
  const [eventName, setEventName] = useState(lens.prop('eventName').get())
  const [eventLanguage, setEventLanguage] = useState(
    lens.prop('eventLanguage').get()
  )
  const [eventDate, setEventDate] = useState(lens.prop('eventDate').get())
  const [eventTime, setEventTime] = useState(lens.prop('eventTime').get())
  const [eventDescription, setEventDescription] = useState(
    lens.prop('eventDescription').get()
  )
  const [eventLink, setEventLink] = useState(lens.prop('eventLink').get())

  const onSave = () => {
    lens.prop('eventCategory').set(eventCategory)
    lens.prop('eventName').set(eventName)
    lens.prop('eventLanguage').set(eventLanguage)
    lens.prop('eventDate').set(eventDate)
    lens.prop('eventTime').set(eventTime)
    lens.prop('eventDescription').set(eventDescription)
    lens.prop('eventLink').set(eventLink)
    save()
  }

  const categoriesDatasource = useArrayDataSource(
    {
      items: categories || ([] as any),
      getId: (i: any) => i.id,
    },
    []
  )

  const languagesArr = getLanguagesArray()

  const languageDataSource = useArrayDataSource(
    {
      items: languagesArr || ([] as any),
      getId: (i: any) => i.code,
    },
    []
  )

  const isMaxLimitReached =
    lens.prop('eventDescription').toProps().value.length >=
    MAX_CHAR_LIMIT.DESCRIPTION

  return (
    <>
      <SidebarPanelHeader title="Edit event" />

      <SidebarPanelMain>
        <FlexRow vPadding="12" alignItems="top">
          <FlexCell grow={1}>
            <LabeledInput
              label="Event Category"
              {...lens.prop('eventCategory').toProps()}
            >
              <PickerInput
                value={eventCategory}
                onValueChange={setEventCategory}
                minBodyWidth={4}
                dataSource={categoriesDatasource}
                selectionMode="single"
                valueType="id"
                placeholder="Select Event Category"
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>
        <FlexRow vPadding="12">
          <FlexCell grow={1}>
            <LabeledInput
              label="Event Name"
              {...lens.prop('eventName').toProps()}
            >
              <TextArea
                autoSize={true}
                maxLength={MAX_CHAR_LIMIT.NAME}
                placeholder="Event Name"
                value={eventName}
                onValueChange={setEventName}
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>
        <FlexRow vPadding="12" alignItems="top">
          <FlexCell grow={1}>
            <LabeledInput
              label="Event Language"
              {...lens.prop('eventLanguage').toProps()}
            >
              <PickerInput
                value={eventLanguage}
                onValueChange={setEventLanguage}
                minBodyWidth={4}
                dataSource={languageDataSource}
                selectionMode="single"
                valueType="entity"
                placeholder="Please select the event language"
                emptyValue={[]}
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow vPadding="12" alignItems="top">
          <FlexCell grow={1}>
            <LabeledInput
              label="Event date"
              {...lens.prop('eventDate').toProps()}
            >
              <DatePicker
                value={eventDate}
                onValueChange={(newValue) => setEventDate(newValue || '')}
                format="DD MMM YYYY"
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow vPadding="12" alignItems="top">
          <FlexCell grow={1}>
            <LabeledInput
              label="Event time"
              {...lens.prop('eventTime').toProps()}
            >
              <TimePicker
                value={eventTime}
                onValueChange={(newValue) =>
                  setEventTime(newValue || eventTime)
                }
                format={24}
                placeholder={'00:00'}
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow vPadding="12">
          <FlexCell grow={1}>
            <LabeledInput
              label="Event Location"
              {...lens.prop('eventLocation').toProps()}
            >
              <TextArea
                maxLength={MAX_CHAR_LIMIT.DESCRIPTION}
                autoSize
                placeholder="Event Location"
                value={address.location || LOCATION_NOTFOUND_MESSAGE}
                onValueChange={() => {}}
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow vPadding="12">
          <FlexCell grow={1}>
            <LabeledInput
              label="Event Description"
              {...lens.prop('eventDescription').toProps()}
            >
              <TextArea
                cx={isMaxLimitReached ? 'textAreaLimitReached' : ''}
                maxLength={MAX_CHAR_LIMIT.DESCRIPTION}
                autoSize
                placeholder="Event Description"
                value={eventDescription}
                onValueChange={setEventDescription}
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow vPadding="12">
          <FlexCell grow={1}>
            <LabeledInput
              label="Event link"
              {...lens.prop('eventLink').toProps()}
            >
              <TextArea
                cx={isMaxLimitReached ? 'textAreaLimitReached' : ''}
                autoSize
                placeholder="Registration or details link"
                maxLength={MAX_CHAR_LIMIT.LINK}
                value={eventLink}
                onValueChange={setEventLink}
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow vPadding="12">
          <FlexSpacer />
          <Button
            caption="Cancel"
            onClick={onCloseEditEventForm}
            fill="white"
            color="sky"
          />
          <Button
            caption="Save"
            onClick={onSave}
            color="sky"
            cx={css.marginBtn}
          />
        </FlexRow>
      </SidebarPanelMain>
    </>
  )
}
